/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Warehouse tables reference", description = "A reference for all the tables and schemas Hightouch creates in your data warehouse, with usage guidance and stability expectations.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Warehouse tables reference",
        description: "A reference for all the tables and schemas Hightouch creates in your data warehouse, with usage guidance and stability expectations.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Usage guidance",
          "depth": 0,
          "id": "usage-guidance"
        }, {
          "value": "Recommended uses",
          "depth": 1,
          "id": "recommended-uses"
        }, {
          "value": "Not recommended",
          "depth": 1,
          "id": "not-recommended"
        }, {
          "value": "Schemas overview",
          "depth": 0,
          "id": "schemas-overview"
        }, {
          "value": "hightouch_planner schema",
          "depth": 0,
          "id": "hightouch_planner-schema"
        }, {
          "value": "Lightning sync engine (CDC)",
          "depth": 1,
          "id": "lightning-sync-engine-cdc"
        }, {
          "value": "Audience snapshots",
          "depth": 1,
          "id": "audience-snapshots"
        }, {
          "value": "Journey tables",
          "depth": 1,
          "id": "journey-tables"
        }, {
          "value": "Identity Resolution outputs",
          "depth": 1,
          "id": "identity-resolution-outputs"
        }, {
          "value": "Identity Resolution internal state",
          "depth": 1,
          "id": "identity-resolution-internal-state"
        }, {
          "value": "AI Decisioning",
          "depth": 1,
          "id": "ai-decisioning"
        }, {
          "value": "hightouch_audit schema",
          "depth": 0,
          "id": "hightouch_audit-schema"
        }, {
          "value": "Warehouse Sync Logs",
          "depth": 1,
          "id": "warehouse-sync-logs"
        }, {
          "value": "Holdout group logs",
          "depth": 1,
          "id": "holdout-group-logs"
        }, {
          "value": "Journey views",
          "depth": 1,
          "id": "journey-views"
        }, {
          "value": "Data extraction",
          "depth": 1,
          "id": "data-extraction"
        }, {
          "value": "Events schema",
          "depth": 0,
          "id": "events-schema"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      strong: "strong",
      code: "code",
      hr: "hr",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      h4: "h4"
    }, _provideComponents(), props.components), {PylonLink, FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "As you use Hightouch, it writes tables back to your data warehouse. These tables record sync activity, audience changes, journey progress, resolved identities, and event data. This page is the single reference for all of them."
      }), "\n", _jsx(_components.p, {
        children: "Each section below explains what each table is, how it's used, and which shouldn't be queried or modified."
      }), "\n", _jsx(_components.h2, {
        id: "usage-guidance",
        children: "Usage guidance"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch writes these tables to your warehouse for observability, debugging, and lightweight reporting. Before building on top of them, keep the following in mind."
      }), "\n", _jsx(_components.h3, {
        id: "recommended-uses",
        children: "Recommended uses"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ad-hoc debugging and auditing."
          }), " Query sync logs to investigate failed rows, inspect audience membership changes, or trace a user's journey path."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lightweight reporting."
          }), " Build dashboards on top of sync run metadata, audience snapshots, or identity resolution outputs to monitor Hightouch activity."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Analytics and activation with Identity Resolution outputs."
          }), " The ", _jsx(_components.code, {
            children: "_resolved"
          }), ", ", _jsx(_components.code, {
            children: "_resolved_identifiers"
          }), ", and ", _jsx(_components.code, {
            children: "_golden_records"
          }), " tables are built for your use. You can query them, register them as dbt sources, or sync from them."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "not-recommended",
        children: "Not recommended"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Wiring tables into production workflows."
          }), " Don't build business-critical pipelines that depend on these tables without ", _jsx(PylonLink, {
            pylonText: "Hi, I want to build a workflow on top of Hightouch warehouse tables. Can we discuss stability expectations?",
            linkText: "talking to us first"
          }), ". Table schemas may change as Hightouch evolves, and we don't guarantee schema stability for all tables."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Writing to Hightouch-managed tables."
          }), " Never insert, update, or delete rows in the ", _jsx(_components.code, {
            children: "hightouch_planner"
          }), " schema (especially ", _jsx(_components.code, {
            children: "JOURNEY_LOG"
          }), " tables and ", _jsx(_components.code, {
            children: "_plan"
          }), "/", _jsx(_components.code, {
            children: "_rejections"
          }), " tables). Hightouch uses these tables during sync and journey execution. Modifying them can cause unexpected behavior."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Depending on internal state tables."
          }), " Tables prefixed with ", _jsx(_components.code, {
            children: "IDR_"
          }), " or ", _jsx(_components.code, {
            children: "IDR_BACKUP_"
          }), " are internal to Identity Resolution. Don't query or reference them in pipelines. Their schemas can change without notice."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Documenting a table here doesn't mean Hightouch is committing to a stable\nschema contract. If you want to build durable integrations on top of these\ntables,", " ", "\n", _jsx(PylonLink, {
            pylonText: "Hi, I'd like to discuss schema stability for Hightouch warehouse tables I'm planning to use in a pipeline.",
            linkText: "reach out to us"
          }), " ", "\nso we can help you find the right approach."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "schemas-overview",
        children: "Schemas overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch uses up to three schemas in your warehouse, depending on which features you enable:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Schema"
            }), _jsx(_components.th, {
              children: "Purpose"
            }), _jsx(_components.th, {
              children: "Created when"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_planner"
              })
            }), _jsx(_components.td, {
              children: "Change data capture, audience snapshots, journey logs, Identity Resolution outputs, AI Decisioning tables"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/syncs/lightning-sync-engine",
                children: "Lightning sync engine"
              }), " is enabled"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_audit"
              })
            }), _jsx(_components.td, {
              children: "Warehouse Sync Logs, holdout group logs, journey views, data extraction reports"
            }), _jsx(_components.td, {
              children: "Lightning sync engine is enabled; individual features populate specific tables"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Custom (per Events source)"
            }), _jsxs(_components.td, {
              children: ["Event data (", _jsx(_components.a, {
                href: "/events/warehouses/schema",
                children: "Hightouch Events"
              }), "); schema name is configurable"]
            }), _jsx(_components.td, {
              children: "An Events source writes to a warehouse destination"
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "hightouch_planner-schema",
        children: "hightouch_planner schema"
      }), "\n", _jsx(_components.h3, {
        id: "lightning-sync-engine-cdc",
        children: "Lightning sync engine (CDC)"
      }), "\n", _jsxs(_components.p, {
        children: ["These tables are created automatically when the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine",
          children: "Lightning sync engine"
        }), " is enabled. They're ", _jsx(_components.strong, {
          children: "required"
        }), " for syncs using the Lightning engine."]
      }), "\n", _jsxs(_components.p, {
        children: ["Change data capture (CDC) is how Hightouch figures out what changed between sync runs. These tables hold the working state Hightouch needs to make that comparison: a snapshot of the model's rows from the previous run, and a record of rows dropped during the current one. You'll rarely query them directly. The occasional exception is ", _jsx(_components.code, {
          children: "*_rejections"
        }), ", which you can inspect to see rows Hightouch dropped during a specific run and why."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "*_plan"
              })
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "Stores a model's query results for a sync run. Hightouch compares this against the previous run to identify added, changed, and removed rows. Table names change with every run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "*_rejections"
              })
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "Stores rows rejected during a sync run. Table names change with every run."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch only keeps the two most recent pairs of ", _jsx(_components.code, {
          children: "_plan"
        }), " and ", _jsx(_components.code, {
          children: "_rejections"
        }), " tables per sync. Because table names change with every run, Hightouch requires write access to the entire schema."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Don't delete tables from ", _jsx(_components.code, {
            children: "hightouch_planner"
          }), ". Removing CDC tables breaks\nchange data capture and requires a ", _jsx(_components.a, {
            href: "/syncs/overview#resync-full-query",
            children: "full\nresync"
          }), " to recover."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For full details, see ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine#warehouse-schemas",
          children: "Lightning sync engine — Warehouse schemas"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "audience-snapshots",
        children: "Audience snapshots"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Optional"
        }), " — must be enabled on the source's ", _jsx(_components.strong, {
          children: "Sync Logs"
        }), " tab. See ", _jsx(_components.a, {
          href: "/customer-studio/audience-snapshots",
          children: "Audience snapshots"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Use this table to see how an audience's membership changed over time — for example, who entered or left a segment between two dates."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "audience_membership"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "ht_row_id"
              }), ", ", _jsx(_components.code, {
                children: "ht_audience_id"
              }), ", ", _jsx(_components.code, {
                children: "ht_timestamp"
              }), ", ", _jsx(_components.code, {
                children: "ht_event_type"
              }), ", ", _jsx(_components.code, {
                children: "ht_split_group"
              })]
            }), _jsxs(_components.td, {
              children: ["Tracks audience membership changes over time. Each row records an ", _jsx(_components.code, {
                children: "enter"
              }), " or ", _jsx(_components.code, {
                children: "exit"
              }), " event for a member."]
            })]
          })
        })]
      }), "\n", _jsx(_components.h3, {
        id: "journey-tables",
        children: "Journey tables"
      }), "\n", _jsxs(_components.p, {
        children: ["Created ", _jsx(_components.strong, {
          children: "automatically"
        }), " when a journey is active. See ", _jsx(_components.a, {
          href: "/customer-studio/journeys/monitor#journey-logs",
          children: "Journeys — Journey logs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Use these tables when you're analyzing a single journey — for example, tracing where members drop off or auditing the path a specific member took. Each journey gets its own set of tables, and each row in these tables represents one visit by one member (the primary key from the journey's parent model)."
      }), "\n", _jsxs(_components.p, {
        children: ["To report across all journeys at once, use the combined ", _jsx(_components.a, {
          href: "#journey-views",
          children: "journey views"
        }), " in the ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schema instead. ", _jsx(_components.code, {
          children: "JOURNEY_LOG"
        }), " and ", _jsx(_components.code, {
          children: "JOURNEY_METADATA"
        }), " views are created automatically; a ", _jsxs(_components.a, {
          href: "#journey_context_log_view",
          children: [_jsx(_components.code, {
            children: "JOURNEY_CONTEXT_LOG"
          }), " view"]
        }), " is also available but must be enabled by Hightouch. Until then, query the per-journey context tables directly when you need journey variable values across journeys."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In these tables, a ", _jsx(_components.strong, {
            children: "node"
          }), " is what you configure as a ", _jsx(_components.strong, {
            children: "tile"
          }), " on the journey\ncanvas. The column names use ", _jsx(_components.code, {
            children: "node"
          }), " because that's the underlying data model;\nthe UI calls them tiles."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Pick the right table for your goal:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Goal"
            }), _jsx(_components.th, {
              children: "Query"
            }), _jsx(_components.th, {
              children: "Schema"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Debug or audit a single journey"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "JOURNEY_LOG_<journey_id>"
              }), ", ", _jsx(_components.code, {
                children: "JOURNEY_METADATA_<journey_id>"
              })]
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_planner"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Report across all journeys at once"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "JOURNEY_LOG_VIEW_<workspace_slug>"
              }), ", ", _jsx(_components.code, {
                children: "JOURNEY_METADATA_VIEW_<workspace_slug>"
              })]
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_audit"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Read journey variable values for a single journey"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "JOURNEY_CONTEXT_LOG_<journey_id>"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_planner"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Read journey variable values across all journeys"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "#journey_context_log_view",
                children: _jsx(_components.code, {
                  children: "JOURNEY_CONTEXT_LOG_VIEW_<workspace_slug>"
                })
              }), " (opt-in)"]
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_audit"
              })
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Don't write to ", _jsx(_components.code, {
            children: "JOURNEY_LOG"
          }), " tables. Hightouch reads and writes them during\njourney execution, so inserting, updating, or deleting rows can cause the\njourney to behave unexpectedly."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.code, {
            children: "journey_id"
          }), " in table names is the journey's UUID with dashes removed."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "journey_log",
        children: "JOURNEY_LOG"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "JOURNEY_LOG_<journey_id>"
        }), " logs the progress of all rows through a given journey. Each time a row enters the journey, moves from one node to another, or exits the journey, Hightouch creates an entry in this table. Since a row can enter a journey more than once, the ", _jsx(_components.code, {
          children: "row_instance_id"
        }), " column uniquely identifies each entry instance as it moves through the journey."]
      }), "\n", _jsx(_components.p, {
        children: "Resetting a journey clears its log table."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The primary key from the journey's parent model that this row represents."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_instance_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["A UUID to uniquely identify a ", _jsx(_components.code, {
                children: "row_id"
              }), " each time it enters the journey."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "run_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the journey run that executed this operation. Internal detail."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "from_node_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The node this action originates from. For moves or exits, it's the node the row moved or exited from. For entries, it's ", _jsx(_components.code, {
                children: "NULL"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "to_node_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The node this action targets. For moves and entries, it's the node the row moved into. For exits, it's ", _jsx(_components.code, {
                children: "NULL"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "The effective timestamp of this operation. This doesn't always represent the actual time the operation occurred — for example, if a row moved due to an event, this is the event timestamp rather than when the warehouse query ran."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_type"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The type of event: ", _jsx(_components.code, {
                children: "moved-to-node"
              }), ", ", _jsx(_components.code, {
                children: "entered-journey"
              }), ", ", _jsx(_components.code, {
                children: "exited-journey-by-criteria"
              }), ", or ", _jsx(_components.code, {
                children: "exited-journey"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_version_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The ID of the journey version this row belongs to. Only present when your workspace uses versioned journey metadata. Used to join log rows to the matching ", _jsx(_components.code, {
                children: "JOURNEY_METADATA"
              }), " snapshot."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "journey_metadata",
        children: "JOURNEY_METADATA"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "JOURNEY_METADATA_<journey_id>"
        }), " describes the shape of the journey: which tiles exist, what they're called, and how they connect. Hightouch writes this table during journey execution, not on save. The exact write behavior depends on your workspace: the table is either replaced on each run, or updated once per journey version with an appended snapshot."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_name"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The name you gave the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_name"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The name you gave the tile."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_type"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsxs(_components.td, {
              children: ["The tile type: ", _jsx(_components.code, {
                children: "entry-cohort"
              }), ", ", _jsx(_components.code, {
                children: "entry-event"
              }), ", ", _jsx(_components.code, {
                children: "sync"
              }), ", ", _jsx(_components.code, {
                children: "segments"
              }), ", ", _jsx(_components.code, {
                children: "segment-branch"
              }), ", ", _jsx(_components.code, {
                children: "time-delay"
              }), ", ", _jsx(_components.code, {
                children: "splits"
              }), ", ", _jsx(_components.code, {
                children: "split-branch"
              }), ", ", _jsx(_components.code, {
                children: "wait-until-event"
              }), ", ", _jsx(_components.code, {
                children: "wait-until-event-branch"
              }), ", ", _jsx(_components.code, {
                children: "context"
              }), " (Set variable), ", _jsx(_components.code, {
                children: "coupon-assignment"
              }), ", and ", _jsx(_components.code, {
                children: "message"
              }), " (Send email, Send SMS, or Send push)."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the tile."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "to_node_id"
              })
            }), _jsx(_components.td, {
              children: "text[]"
            }), _jsx(_components.td, {
              children: "The IDs of every tile this one connects out to."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_version_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsxs(_components.td, {
              children: ["The ID of the journey version this snapshot belongs to. Only present when your workspace uses versioned journey metadata (the appended-snapshot behavior described above). Used to join metadata to the matching ", _jsx(_components.code, {
                children: "JOURNEY_LOG"
              }), " rows."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "created_at"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "When Hightouch wrote this snapshot. Only present with versioned journey metadata."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "journey_context_log",
        children: "JOURNEY_CONTEXT_LOG"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "JOURNEY_CONTEXT_LOG_<journey_id>"
        }), " records the value of each ", _jsx(_components.a, {
          href: "/customer-studio/journeys/tiles#set-variable",
          children: "journey variable"
        }), " as members pass through Set variable tiles. Filter it to build audiences of members who reached a specific branch — for example, everyone whose ", _jsx(_components.code, {
          children: "vip_user"
        }), " variable was set to ", _jsx(_components.code, {
          children: "true"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The primary key from the journey's parent model that this row represents."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_instance_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "A unique ID assigned each time this member enters the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "run_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the journey run that recorded this value. Internal detail."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the Set variable tile that recorded this value."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "entered_at"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "When the member reached the Set variable tile."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "inserted_at"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "When Hightouch wrote the row. If multiple Set variable tiles assign the same variable, the row with the latest value wins."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[variable_name]"
              })
            }), _jsx(_components.td, {
              children: "dynamic"
            }), _jsx(_components.td, {
              children: "One column per journey variable. The column's type matches the variable's type."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "identity-resolution-outputs",
        children: "Identity Resolution outputs"
      }), "\n", _jsxs(_components.p, {
        children: ["Created ", _jsx(_components.strong, {
          children: "automatically"
        }), " when an identity graph runs. See ", _jsx(_components.a, {
          href: "/identity-resolution/overview",
          children: "Identity Resolution"
        }), " for details."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, these tables are written to ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), ". If you configure an output schema for the identity graph, Hightouch writes them to that schema instead."]
      }), "\n", _jsx(_components.p, {
        children: "Use these tables to power analytics and activation on resolved identities. They're the recommended starting point for anything you build on top of Identity Resolution: query them directly, register them as dbt sources, or sync from them."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<output_prefix>_resolved"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "ht_id"
              }), ", ", _jsx(_components.code, {
                children: "source"
              }), ", ", _jsx(_components.code, {
                children: "primary_key"
              }), ", ", _jsx(_components.code, {
                children: "latest_timestamp"
              })]
            }), _jsx(_components.td, {
              children: "Maps every input row to a resolved identity."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<output_prefix>_resolved_identifiers"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "ht_id"
              }), ", ", _jsx(_components.code, {
                children: "identifier"
              }), ", ", _jsx(_components.code, {
                children: "value"
              }), ", ", _jsx(_components.code, {
                children: "first_timestamp"
              }), ", ", _jsx(_components.code, {
                children: "last_timestamp"
              }), ", ", _jsx(_components.code, {
                children: "count"
              })]
            }), _jsx(_components.td, {
              children: "All identifier values associated with each resolved identity."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<output_prefix>_unresolved"
              })
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "Input rows that couldn't be processed, typically due to duplicate primary keys."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<output_prefix>_golden_records"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "ht_id"
              }), ", plus one column per configured field"]
            }), _jsxs(_components.td, {
              children: ["One row per identity with canonical field values. Only created when ", _jsx(_components.a, {
                href: "/identity-resolution/golden-record",
                children: "Golden Record"
              }), " is enabled."]
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["These four output tables ", _jsx(_components.strong, {
            children: "are designed for your use"
          }), ". You can safely query\nthem, reference them in dbt, or build views on top of them. For long-term\nstability, we recommend creating a view or derived table rather than\ntransforming them in place."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "identity-resolution-internal-state",
        children: "Identity Resolution internal state"
      }), "\n", _jsxs(_components.p, {
        children: ["Created ", _jsx(_components.strong, {
          children: "automatically"
        }), " as part of identity graph processing. These tables support incremental resolution and graph consistency behind the scenes. They aren't meant for your use."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "IDR_*"
              }), " / ", _jsx(_components.code, {
                children: "IDR_BACKUP_*"
              })]
            }), _jsx(_components.td, {
              children: "Internal tables for incremental processing and graph consistency."
            })]
          })
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Don't query or modify these tables. They're internal implementation details\nand can change without notice."
        })
      }), "\n", _jsx(_components.h3, {
        id: "ai-decisioning",
        children: "AI Decisioning"
      }), "\n", _jsxs(_components.p, {
        children: ["Created ", _jsx(_components.strong, {
          children: "automatically"
        }), " per agent when using Hightouch-assigned groups. See ", _jsx(_components.a, {
          href: "/ai-decisioning/group-assignment#experiment-groups-table-in-the-warehouse",
          children: "AI Decisioning — Group assignment"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use this table to analyze experiment results: compare how the ", _jsx(_components.code, {
          children: "treatment"
        }), ", ", _jsx(_components.code, {
          children: "holdout"
        }), ", and ", _jsx(_components.code, {
          children: "customer_managed"
        }), " groups performed to measure AI Decisioning's impact."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "de_user_experiment_groups_<agent_id>"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "user_id"
              }), ", ", _jsx(_components.code, {
                children: "user_hash"
              }), ", ", _jsx(_components.code, {
                children: "experiment"
              }), ", ", _jsx(_components.code, {
                children: "first_seen_at"
              }), ", ", _jsx(_components.code, {
                children: "last_seen_at"
              })]
            }), _jsxs(_components.td, {
              children: ["Tracks which experiment bucket (", _jsx(_components.code, {
                children: "treatment"
              }), ", ", _jsx(_components.code, {
                children: "holdout"
              }), ", ", _jsx(_components.code, {
                children: "customer_managed"
              }), ") each user belongs to over time."]
            })]
          })
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "hightouch_audit-schema",
        children: "hightouch_audit schema"
      }), "\n", _jsx(_components.h3, {
        id: "warehouse-sync-logs",
        children: "Warehouse Sync Logs"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Optional"
        }), " — must be enabled per source or per sync. See ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use these to audit sync outcomes: investigate why specific rows failed (", _jsx(_components.code, {
          children: "sync_changelog"
        }), "), check the latest status of each row (", _jsx(_components.code, {
          children: "sync_snapshot"
        }), "), or report on run-level metrics like planned, succeeded, and failed counts (", _jsx(_components.code, {
          children: "sync_runs"
        }), ")."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_changelog"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "sync_id"
              }), ", ", _jsx(_components.code, {
                children: "row_id"
              }), ", ", _jsx(_components.code, {
                children: "op_type"
              }), ", ", _jsx(_components.code, {
                children: "status"
              }), ", ", _jsx(_components.code, {
                children: "failure_reason"
              }), ", ", _jsx(_components.code, {
                children: "fields"
              })]
            }), _jsx(_components.td, {
              children: "A log of every operation across all sync runs. Each synced row gets one entry per run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_snapshot"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "sync_id"
              }), ", ", _jsx(_components.code, {
                children: "row_id"
              }), ", ", _jsx(_components.code, {
                children: "op_type"
              }), ", ", _jsx(_components.code, {
                children: "status"
              }), ", ", _jsx(_components.code, {
                children: "failure_reason"
              }), ", ", _jsx(_components.code, {
                children: "fields"
              })]
            }), _jsx(_components.td, {
              children: "The latest status of each row across all syncs. Replaced after each run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_runs"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "sync_id"
              }), ", ", _jsx(_components.code, {
                children: "sync_run_id"
              }), ", ", _jsx(_components.code, {
                children: "model_name"
              }), ", ", _jsx(_components.code, {
                children: "started_at"
              }), ", ", _jsx(_components.code, {
                children: "finished_at"
              }), ", ", _jsx(_components.code, {
                children: "status"
              })]
            }), _jsx(_components.td, {
              children: "Metadata for each sync run, including row counts for planned, attempted, succeeded, and failed operations."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "holdout-group-logs",
        children: "Holdout group logs"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Optional"
        }), " — requires a feature flag and must be enabled on the source's ", _jsx(_components.strong, {
          children: "Sync Logs"
        }), " tab. See ", _jsx(_components.a, {
          href: "/customer-studio/experiments#holdout-group-logs",
          children: "Experiments — Holdout group logs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Use this table after a campaign to measure holdout performance: compare outcomes for held-out members against those who received the campaign."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "audience_holdout"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "sync_id"
              }), ", ", _jsx(_components.code, {
                children: "sync_run_id"
              }), ", ", _jsx(_components.code, {
                children: "model_id"
              }), ", ", _jsx(_components.code, {
                children: "row_id"
              }), ", ", _jsx(_components.code, {
                children: "fields"
              }), ", ", _jsx(_components.code, {
                children: "split_group"
              })]
            }), _jsx(_components.td, {
              children: "Logs rows excluded from a sync due to holdout group membership, for post-campaign analysis."
            })]
          })
        })]
      }), "\n", _jsx(_components.h3, {
        id: "journey-views",
        children: "Journey views"
      }), "\n", _jsxs(_components.p, {
        children: ["Created ", _jsx(_components.strong, {
          children: "automatically"
        }), " when journeys are active. These are read-only views that stitch every journey's ", _jsx(_components.a, {
          href: "#journey-tables",
          children: "per-journey tables"
        }), " together, so you can report across all journeys at once without querying and joining each one manually."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "JOURNEY_LOG"
        }), " and ", _jsx(_components.code, {
          children: "JOURNEY_METADATA"
        }), " views are created automatically for every workspace. A ", _jsxs(_components.a, {
          href: "#journey_context_log_view",
          children: ["combined ", _jsx(_components.code, {
            children: "JOURNEY_CONTEXT_LOG"
          }), " view"]
        }), " is also available but is off by default."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Find your workspace slug under ", _jsx(_components.strong, {
            children: "Settings → Workspace → Workspace slug"
          }), ".\nLowercase the slug and replace any non-alphanumeric character (such as a dash)\nwith an underscore when querying (for example, ", _jsx(_components.code, {
            children: "My-Workspace"
          }), " becomes\n", _jsx(_components.code, {
            children: "my_workspace"
          }), ")."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "journey_log_view",
        children: "JOURNEY_LOG_VIEW"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "JOURNEY_LOG_VIEW_<workspace_slug>"
        }), " combines all ", _jsx(_components.code, {
          children: "JOURNEY_LOG"
        }), " tables into a workspace-level view of row movement across all journeys. Hightouch rebuilds the view during journey runs on an as-needed basis: when a new log table is added, when schema or version columns require it, or when the view is marked stale. Journey removal also triggers a rebuild."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_table"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The name of the table this row came from: ", _jsx(_components.code, {
                children: "journey_log_<journey_id>"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The primary key from the journey's parent model that this row represents."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_instance_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["A UUID to uniquely identify a ", _jsx(_components.code, {
                children: "row_id"
              }), " each time it enters the journey."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "run_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the journey run that executed this operation. Internal detail."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "from_node_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The node this action originates from. For moves or exits, it's the node the row moved or exited from. For entries, it's ", _jsx(_components.code, {
                children: "NULL"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "to_node_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The node this action targets. For moves and entries, it's the node the row moved into. For exits, it's ", _jsx(_components.code, {
                children: "NULL"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "The effective timestamp of this operation. This doesn't always represent the actual time the operation occurred — for example, if a row moved due to an event, this is the event timestamp rather than when the warehouse query ran."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_type"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The type of event: ", _jsx(_components.code, {
                children: "moved-to-node"
              }), ", ", _jsx(_components.code, {
                children: "entered-journey"
              }), ", ", _jsx(_components.code, {
                children: "exited-journey-by-criteria"
              }), ", or ", _jsx(_components.code, {
                children: "exited-journey"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_version_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the journey version this row belongs to. Only present when your workspace uses versioned journey metadata."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "journey_metadata_view",
        children: "JOURNEY_METADATA_VIEW"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "JOURNEY_METADATA_VIEW_<workspace_slug>"
        }), " combines every journey's ", _jsx(_components.code, {
          children: "JOURNEY_METADATA"
        }), " table so you can see the shape of every journey in the workspace at once. Hightouch rebuilds the view during journey runs on an as-needed basis: when a new metadata table is added, when an existing one is converted, or when the view falls out of sync with the underlying tables."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_table"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The name of the table this row came from: ", _jsx(_components.code, {
                children: "journey_metadata_<journey_id>"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_name"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The name you gave the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_name"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The name you gave the tile."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_type"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsxs(_components.td, {
              children: ["The tile type: ", _jsx(_components.code, {
                children: "entry-cohort"
              }), ", ", _jsx(_components.code, {
                children: "entry-event"
              }), ", ", _jsx(_components.code, {
                children: "sync"
              }), ", ", _jsx(_components.code, {
                children: "segments"
              }), ", ", _jsx(_components.code, {
                children: "segment-branch"
              }), ", ", _jsx(_components.code, {
                children: "time-delay"
              }), ", ", _jsx(_components.code, {
                children: "splits"
              }), ", ", _jsx(_components.code, {
                children: "split-branch"
              }), ", ", _jsx(_components.code, {
                children: "wait-until-event"
              }), ", ", _jsx(_components.code, {
                children: "wait-until-event-branch"
              }), ", ", _jsx(_components.code, {
                children: "context"
              }), " (Set variable), ", _jsx(_components.code, {
                children: "coupon-assignment"
              }), ", and ", _jsx(_components.code, {
                children: "message"
              }), " (Send email, Send SMS, or Send push)."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the tile."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "to_node_id"
              })
            }), _jsx(_components.td, {
              children: "text[]"
            }), _jsx(_components.td, {
              children: "The IDs of every tile this one connects out to."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_version_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the journey version this snapshot belongs to. Only present when your workspace uses versioned journey metadata."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "created_at"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "When Hightouch wrote this snapshot. Only present with versioned journey metadata."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "journey_context_log_view",
        children: "JOURNEY_CONTEXT_LOG_VIEW"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Global journey context log view"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "JOURNEY_CONTEXT_LOG_VIEW_<workspace_slug>"
        }), " combines every journey's ", _jsx(_components.code, {
          children: "JOURNEY_CONTEXT_LOG"
        }), " table so you can query journey variable values across all journeys in the workspace at once. Hightouch rebuilds the view when a new context log table is added or when a journey adds a new variable. Columns for renamed or removed variables are kept in the view so historical values remain queryable."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The primary key from the journey's parent model that this row represents."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_instance_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "A unique ID assigned each time this member enters the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "run_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the journey run that recorded this value. Internal detail."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "node_id"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the Set variable tile that recorded this value."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "entered_at"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "When the member reached the Set variable tile."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "inserted_at"
              })
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "When Hightouch wrote the row. If multiple Set variable tiles assign the same variable, the row with the latest value wins."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "journey_id"
              })
            }), _jsx(_components.td, {
              children: "text"
            }), _jsx(_components.td, {
              children: "The ID of the journey."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_table"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The name of the table this row came from: ", _jsx(_components.code, {
                children: "journey_context_log_<journey_id>"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[variable_name]"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "One column per journey variable across all journeys. Unlike the per-journey table, the view casts every variable to string so the combined columns align across journeys."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "data-extraction",
        children: "Data extraction"
      }), "\n", _jsxs(_components.p, {
        children: ["Created ", _jsx(_components.strong, {
          children: "automatically"
        }), " when data extraction is enabled for a destination. See ", _jsx(_components.a, {
          href: "/syncs/data-extraction",
          children: "Data extraction"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use these to see the audiences Hightouch maintains in each ad platform. Query the combined ", _jsx(_components.code, {
          children: "external_audiences_metadata_v1"
        }), " view to report on audience sizes and refresh times across destinations."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "{destination-type}_{destination-id}_audiences"
              })
            }), _jsx(_components.td, {
              children: "Varies by ad platform"
            }), _jsx(_components.td, {
              children: "One table per enabled destination. Schema mirrors the ad platform's API response."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "external_audiences_metadata_v1"
              }), " (view)"]
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "ad_account_id"
              }), ", ", _jsx(_components.code, {
                children: "audience_id"
              }), ", ", _jsx(_components.code, {
                children: "audience_name"
              }), ", ", _jsx(_components.code, {
                children: "audience_size"
              }), ", ", _jsx(_components.code, {
                children: "ht_fetched_at"
              })]
            }), _jsx(_components.td, {
              children: "A combined, standardized view across all destination tables."
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "events-schema",
        children: "Events schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/warehouses/schema",
          children: "Hightouch Events"
        }), " writes event data into a ", _jsx(_components.strong, {
          children: "configurable schema"
        }), " — not ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " or ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), ". The schema name defaults to an auto-generated value based on the event source but can be configured in destination settings. Tables are created ", _jsx(_components.strong, {
          children: "automatically"
        }), " when an Events source writes to a warehouse destination."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use these tables to analyze collected behavioral data directly in your warehouse — for example, build models on ", _jsx(_components.code, {
          children: "tracks"
        }), " events or join ", _jsx(_components.code, {
          children: "identifies"
        }), " traits into your audiences."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Table"
            }), _jsx(_components.th, {
              children: "Key columns"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "identifies"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "id"
              }), ", ", _jsx(_components.code, {
                children: "anonymous_id"
              }), ", ", _jsx(_components.code, {
                children: "user_id"
              }), ", ", _jsx(_components.code, {
                children: "<traits>"
              }), ", ", _jsx(_components.code, {
                children: "timestamp"
              })]
            }), _jsxs(_components.td, {
              children: ["All ", _jsx(_components.code, {
                children: "identify"
              }), " events. Trait keys become separate columns."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "tracks"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "id"
              }), ", ", _jsx(_components.code, {
                children: "anonymous_id"
              }), ", ", _jsx(_components.code, {
                children: "user_id"
              }), ", ", _jsx(_components.code, {
                children: "event"
              }), ", ", _jsx(_components.code, {
                children: "event_text"
              }), ", ", _jsx(_components.code, {
                children: "timestamp"
              })]
            }), _jsxs(_components.td, {
              children: ["All ", _jsx(_components.code, {
                children: "track"
              }), " events."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Per-event tables"
            }), _jsx(_components.td, {
              children: "Varies"
            }), _jsx(_components.td, {
              children: "A separate table for each track event type, with event properties as columns. Can be disabled."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "pages"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "id"
              }), ", ", _jsx(_components.code, {
                children: "anonymous_id"
              }), ", ", _jsx(_components.code, {
                children: "user_id"
              }), ", ", _jsx(_components.code, {
                children: "<properties>"
              }), ", ", _jsx(_components.code, {
                children: "timestamp"
              })]
            }), _jsxs(_components.td, {
              children: ["All ", _jsx(_components.code, {
                children: "page"
              }), " events."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "screens"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "id"
              }), ", ", _jsx(_components.code, {
                children: "anonymous_id"
              }), ", ", _jsx(_components.code, {
                children: "user_id"
              }), ", ", _jsx(_components.code, {
                children: "<properties>"
              }), ", ", _jsx(_components.code, {
                children: "timestamp"
              })]
            }), _jsxs(_components.td, {
              children: ["All ", _jsx(_components.code, {
                children: "screen"
              }), " events."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groups"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "id"
              }), ", ", _jsx(_components.code, {
                children: "anonymous_id"
              }), ", ", _jsx(_components.code, {
                children: "user_id"
              }), ", ", _jsx(_components.code, {
                children: "group_id"
              }), ", ", _jsx(_components.code, {
                children: "<traits>"
              }), ", ", _jsx(_components.code, {
                children: "timestamp"
              })]
            }), _jsxs(_components.td, {
              children: ["All ", _jsx(_components.code, {
                children: "group"
              }), " events."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For the full column-level schema, see ", _jsx(_components.a, {
          href: "/events/warehouses/schema",
          children: "Events warehouse schema"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
